// CUSTOM: personalised-recharge-session.js
// Recharge auth and session management:
//   customerIdManager, sessionManager, eventBus,
//   waitForRechargeSDK, isPortalUrl, checkForPortalReturn, refreshSessionTokensOnly

(function() {
  // Ensure namespace exists
  window.personalised = window.personalised || {};

  /**
   * Customer ID Manager - Encapsulates customer ID access with a getter/setter closure
   * to prevent race conditions and accidental overwrites
   */
  const customerIdManager = (function() {
    // Internal storage for customer ID
    let _customerId = null;

    return {
      /**
       * Gets the current customer ID
       * @returns {string|null} The customer ID or null if not set
       */
      get() {
        return _customerId;
      },

      /**
       * Sets the customer ID
       * @param {string|null} id - The customer ID to set
       * @returns {boolean} Success status
       */
      set(id) {
        // Allow null/undefined to reset
        if (id === null || id === undefined) {
          _customerId = null;
          if (window.personalised?.debugMode) console.debug('[CustomerIdManager] Customer ID reset');
          return true;
        }
        
        // Validate ID format before setting
        if (typeof id === 'string' || typeof id === 'number') {
          _customerId = String(id);
          if (window.personalised?.debugMode) console.debug('[CustomerIdManager] Customer ID set successfully');
          return true;
        } else {
          if (window.personalised?.debugMode) console.warn('[CustomerIdManager] Invalid customer ID format:', typeof id);
          return false;
        }
      }
    };
  })();
  
  // Define customerIdManager on personalised namespace for internal use
  window.personalised.customerIdManager = customerIdManager;
  
  // Define property for backward compatibility
  Object.defineProperty(window.personalised, 'customerId', {
    get() {
      return customerIdManager.get();
    },
    set(value) {
      customerIdManager.set(value);
    },
    enumerable: true,
    configurable: true
  });

  /**
   * Session Manager module to centralize Recharge session management
   */
  const sessionManager = (function() {
    // Internal session storage
    let currentSession = null;
    // Promise memoization to prevent concurrent session fetches
    let _sessionPromise = null;
    
    /**
     * Validates if a session has the required properties for SDK v1.38.0
     * Note: Using loginShopifyAppProxy return value format
     * 
     * @param {Object} session - The session to validate
     * @returns {boolean} True if valid, false otherwise
     */
    const isValidSession = (session) => {
      return session && 
             typeof session === 'object' && 
             typeof session.apiToken === 'string' &&
             session.apiToken.length > 0;
    };
    
    return {
      /**
       * Gets the current Recharge session by calling loginShopifyAppProxy
       * Note: Uses loginShopifyAppProxy instead of getSession as per SDK v1.38.0 requirements
       * 
       * @param {Object|boolean} options - Options object or boolean for backwards compatibility
       * @param {boolean} [options.forceRefresh] - Force a new session even if one exists
       * @returns {Promise<Object|null>} The session object or null if unavailable
       */
      async getSession(options = false) {
        // Handle legacy boolean parameter for backwards compatibility
        const forceRefresh = typeof options === 'boolean' ? options : options.forceRefresh || false;
        
        // Check for session freshness flag
        // PR-4.3: route through storageManager (single authority for SESSION_FRESH key)
        const isSessionFresh = window.personalised.storageManager?.isSessionFresh();
        if (isSessionFresh) {
          if (window.personalised?.debugMode) console.debug('[SessionManager] Session freshness flag detected, clearing flag and forcing refresh');
          window.personalised.storageManager?.clearSessionFresh();
          // Continue to fetch a fresh session by not returning early
        }
        // Use cached session if valid and not forcing refresh and no freshness flag
        else if (!forceRefresh && isValidSession(currentSession)) {
          if (window.personalised?.debugMode) console.debug('[SessionManager] Returning cached session');
          return currentSession;
        }

        // Avoid duplicate in-progress session fetches
        if (_sessionPromise && !forceRefresh) {
          if (window.personalised?.debugMode) console.debug('[SessionManager] Returning in-progress session promise');
          return _sessionPromise;
        }

        if (window.personalised?.debugMode) console.debug('[SessionManager] Starting new session fetch');

        // Start new session fetch
        _sessionPromise = (async () => {
          try {
            // Wait for Recharge SDK to be ready
            if (!window.rechargeInitialized && !window.recharge?.auth?.loginShopifyAppProxy) {
              if (window.rechargeInitializing) {
                if (window.personalised?.debugMode) console.debug('[SessionManager] Waiting for Recharge SDK to initialize...');
                await new Promise((resolve, reject) => {
                  const timeoutId = setTimeout(() => reject(new Error('Recharge SDK init timeout')), 5000);
                  window.addEventListener('recharge:ready', () => {
                    clearTimeout(timeoutId);
                    resolve();
                  }, { once: true });
                });
              } else {
                console.error('[SessionManager] SDK not initializing and not ready');
                return null;
              }
            }

            // Ensure SDK method is now available
            if (!window.recharge?.auth?.loginShopifyAppProxy) {
              console.error('[SessionManager] Recharge SDK still unavailable');
              return null;
            }

            // Try to get session via loginShopifyAppProxy (SDK v1.38.0)
            const session = await window.recharge.auth.loginShopifyAppProxy();
            if (isValidSession(session)) {
              currentSession = session;
              if (window.personalised?.debugMode) console.debug('[SessionManager] Session obtained and cached');
            } else {
              if (window.personalised?.debugMode) console.warn('[SessionManager] Invalid session from loginShopifyAppProxy');
            }

            return currentSession;
          } catch (error) {
            console.error('[SessionManager] Failed to retrieve session:', error);
            return null;
          } finally {
            // Clear the promise reference once completed
            _sessionPromise = null;
          }
        })();
        
        return _sessionPromise;
      },
      
      /**
       * Updates the current session
       * @param {Object} session - New session object 
       * @returns {boolean} Success status
       */
      updateSession(session) {
        if (!isValidSession(session)) {
          if (window.personalised?.debugMode) console.warn('[SessionManager] Attempted to update with invalid session');
          return false;
        }
        
        currentSession = session;
        if (window.personalised?.debugMode) console.debug('[SessionManager] Session updated successfully');
        return true;
      },
      
      /**
       * Checks if we have a valid session
       * @returns {boolean} True if the current session is valid
       */
      hasValidSession() {
        return isValidSession(currentSession);
      },
      
      /**
       * Checks if the session has authentication (customerId)
       * @returns {boolean} True if the session has a customerId
       */
      isAuthenticatedSession() {
        return isValidSession(currentSession) && !!currentSession.customerId;
      },
      
      /**
       * Gets customer ID from session if available
       * @returns {string|null} The customer ID or null
       */
      getCustomerId() {
        return this.isAuthenticatedSession() ? currentSession.customerId : null;
      }
    };
  })();
  
  // Register sessionManager in the personalised namespace
  window.personalised.sessionManager = sessionManager;

  // Event Bus
  const eventBus = (function() {
    // Create a dedicated DOM element to act as our event target
    const eventTarget = document.createElement('div');
    eventTarget.id = 'personalised-event-target';
    
    /**
     * Registers an event listener for personalised events
     * @param {string} event - The event name to listen for
     * @param {function} callback - The callback to execute when the event is triggered
     */
    function on(event, callback) {
      const wrappedCallback = (e) => {
        try {
          callback(e.detail);
        } catch (err) {
          console.error(`Error in event '${event}' callback:`, err);
          // Error is caught and logged but doesn't break the event chain
        }
      };
      
      // Store the original callback reference for potential removal
      if (!callback._wrappedFn) {
        callback._wrappedFn = wrappedCallback;
      }
      
      eventTarget.addEventListener(event, wrappedCallback);
    }
    
    /**
     * Removes an event listener previously registered with on()
     * @param {string} event - The event name
     * @param {function} callback - The callback to remove
     */
    function off(event, callback) {
      // Remove the wrapper function that was created in on()
      if (callback && callback._wrappedFn) {
        eventTarget.removeEventListener(event, callback._wrappedFn);
        delete callback._wrappedFn;
      }
    }
    
    /**
     * Dispatches a personalised event with optional data
     * @param {string} event - The event name to dispatch
     * @param {any} data - The data to include with the event
     */
    function emit(event, data) {
      const customEvent = new CustomEvent(event, {
        bubbles: false,
        cancelable: true,
        detail: data
      });
      
      eventTarget.dispatchEvent(customEvent);
    }
    
    // Public API
    return {
      on,
      off,
      emit
    };
  })();

  window.personalised.eventBus = eventBus;

  /**
   * Waits for the Recharge SDK to be fully initialized and ready to use
   * @param {number} timeout - Maximum time to wait in milliseconds (default: 10000ms)
   * @returns {Promise<boolean>} - Resolves true if SDK is ready, rejects on timeout or failure
   */
  function waitForRechargeSDK(timeout = 10000) {
    return new Promise((resolve, reject) => {
      if (window.rechargeInitialized && window.recharge) {
        console.debug('[SDK Wait] Recharge already initialized');
        return resolve(true);
      }

      const timeoutId = setTimeout(() => {
        console.error('[SDK Wait] Recharge SDK timeout');
        reject(new Error('Recharge SDK did not initialize in time'));
      }, timeout);

      // Listen if it's already initializing
      if (window.rechargeInitializing) {
        window.addEventListener('recharge:ready', () => {
          clearTimeout(timeoutId);
          console.debug('[SDK Wait] Recharge SDK ready via event');
          resolve(true);
        }, { once: true });
        return;
      }

      // Poll until it begins initializing, then hook into event
      let attempts = 0;
      const intervalId = setInterval(() => {
        attempts++;

        if (window.rechargeInitialized && window.recharge) {
          clearInterval(intervalId);
          clearTimeout(timeoutId);
          console.debug('[SDK Wait] Recharge SDK became available via poll');
          return resolve(true);
        }

        if (window.rechargeInitializing) {
          clearInterval(intervalId);
          window.addEventListener('recharge:ready', () => {
            clearTimeout(timeoutId);
            console.debug('[SDK Wait] Recharge SDK ready via event after polling');
            resolve(true);
          }, { once: true });
        }
      }, 100);
    });
  }

  /**
   * Checks if a URL contains the portal path
   * @param {string} [url] - Optional URL to check, defaults to current location
   * @returns {boolean} - True if URL contains portal path
   */
  function isPortalUrl(url) {
    return (url || window.location.href).includes('/tools/recurring/portal/');
  }
  
  /**
   * Lightweight wrapper for checkForPortalReturn that delegates to the version in personalised-login-verification.js
   * @returns {boolean} True if portal return was detected
   */
  function checkForPortalReturn() {
    // Delegate to the global implementation in personalised-login-verification.js
    // This prevents duplication of logic while ensuring the function can still be called from utils
    if (window.personalised?.checkForPortalReturn) {
      return window.personalised.checkForPortalReturn();
    }
    
    // Minimal fallback only if login verification hasn't loaded yet
    // Simply sets the freshness flag without doing URL rewriting or duplicate logging
    const portalReturnParam = new URLSearchParams(window.location.search).get('portal_return');
    if (portalReturnParam === 'true' || isPortalUrl(window.location.href)) {
      console.debug('[Personalised] Portal return detected (utils fallback) — setting session freshness flag');
      window.personalised.storageManager?.setSessionFresh(); // PR-4.3
      return true;
    }
    
    return false;
  }
  
  /**
   * Refresh only Recharge session and bundle cache — used when returning from portal.
   * Preserves user mode, subscription ID, and login state.
   * @param {string} reason - Reason for refreshing the session (for logging)
   */
  function refreshSessionTokensOnly(reason = 'manual') {
    // Remove Recharge session from memory and storage
    if (window.personalised.sessionManager) {
      delete window.personalised.sessionManager.currentSession;
    }

    window.personalised.storageManager?.clearRechargeToken(); // PR-4.3
    window.personalised.storageManager?.setSessionFresh();    // PR-4.3

    // Clear localStorage bundle caches only — do not clear mode or subscription_id
    localStorage.removeItem('personalised_bundle_items');
    localStorage.removeItem('personalised_bundle_summary');
    localStorage.removeItem('personalised_bundle_error');

    // Clear in-memory state if applicable
    if (window.personalised.bundleState?.reset) {
      window.personalised.bundleState.reset();
    }

    console.log(`[Personalised] Session and bundle cache refreshed (${reason}). Mode and subscription preserved.`);
  }

  // Register SDK and portal utilities on the utils namespace
  window.personalised.utils = window.personalised.utils || {};
  window.personalised.utils.waitForRechargeSDK = waitForRechargeSDK;
  window.personalised.utils.isPortalUrl = isPortalUrl;
  window.personalised.utils.checkForPortalReturn = checkForPortalReturn;
  window.personalised.utils.refreshSessionTokensOnly = refreshSessionTokensOnly;

  // Check for portal return on page load
  document.addEventListener('DOMContentLoaded', () => {
    if (window.personalised?.utils?.checkForPortalReturn) {
      window.personalised.utils.checkForPortalReturn();
    }
  });
})();
